import {dynamic} from '@github-ui/dynamic'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Hero} from '@primer/react-brand/esm'
import {clsx} from 'clsx'

import {getImageSources, MAX_CONTENT_WIDTH} from '@github-ui/swp-core/lib/utils/images'

import {AssetTypeEnum} from '../../types'
import baseStyles from '../../styles/base.module.css'
import {hasExternalVideo} from '../../utils/asset-helpers'
import {useDitherBackground} from '../../hooks/useDitherBackground'
import {AssetVideo} from '../Video'
import type {FlexSuiteStackedHeroProps} from './types'
import {HeroContent} from './HeroContent'
import styles from './FlexSuiteHero.module.css'

// Client-only: keeps three.js and gsap out of the server bundle and initial JS chunk.
// HeroCopilotHead is a decorative WebGL effect that is not needed for initial render.
const HeroCopilotHead = dynamic(
  async () => {
    const m = await import('./HeroCopilotHead')
    return {default: m.HeroCopilotHead}
  },
  {ssr: false, displayName: 'HeroCopilotHead'},
)

/**
 * Stacked hero: center-aligned with a block-end image or video
 */
export function FlexSuiteHeroStacked({fields}: {fields: FlexSuiteStackedHeroProps}) {
  const {
    heroAsset,
    heroPosition,
    imageContainerClassName: consumerImageContainerClassName,
    options: {glassBorder, showCopilotHead},
  } = fields
  const {assetType, optionImage, optionVideo, optionPoster, autoplay, videoSource} = heroAsset?.fields ?? {}
  const externalVideo = hasExternalVideo(assetType, videoSource)
  const showMediaDither = !(heroPosition === 'Fill' || externalVideo)
  const {containerRef, containerEl, DitherBackground} = useDitherBackground({
    enabled: showMediaDither,
  })
  const headingClassName = showCopilotHead ? styles.heroCopilotHeading : undefined
  const mediaPosition = 'block-end'
  const mediaPadding = heroPosition === 'Center' ? 'all' : undefined
  const imageContainerClassName = clsx(
    styles.heroImageWrapper,
    heroPosition !== 'Fill' && styles.removeBorder,
    showMediaDither && styles.withDither,
    externalVideo && styles.externalVideoGradient,
    heroPosition === 'Fill' && styles.fullBleed,
    consumerImageContainerClassName,
  )

  return (
    <>
      <HeroContent
        {...fields}
        data-testid="hero-stacked"
        className={baseStyles.gridFrameBottom}
        imageContainerClassName={imageContainerClassName}
        imageContainerRef={containerRef as unknown as React.RefObject<HTMLDivElement | null>}
        headingClassName={headingClassName}
      >
        {assetType === AssetTypeEnum.enum.Image && optionImage && (
          <Hero.Image
            className={clsx(heroPosition !== 'Fill' && styles.heroAsset, glassBorder && styles.glassBorder)}
            position={mediaPosition}
            padding={mediaPadding}
            as="picture"
            src={`${optionImage.fields.file.url}?fm=webp`}
            sources={getImageSources(optionImage.fields.file.url, {
              maxWidth: MAX_CONTENT_WIDTH,
              disableDprScaling: true,
            })}
            alt={optionImage.fields.description ?? ''}
            enableBorder={false}
            fetchPriority="high"
          />
        )}
        {assetType !== AssetTypeEnum.enum.Image && (optionVideo || videoSource) && (
          <Hero.Video
            className={clsx(heroPosition !== 'Fill' && styles.heroAsset, glassBorder && styles.glassBorder)}
            position={mediaPosition}
            padding={mediaPadding}
          >
            <AssetVideo
              assetType={assetType}
              optionVideo={optionVideo}
              optionPoster={optionPoster}
              autoplay={autoplay}
              videoSource={videoSource}
              priority="high"
            />
          </Hero.Video>
        )}
      </HeroContent>
      {showCopilotHead && containerEl ? (
        <ErrorBoundary boundaryName="FlexSuiteHeroStackedHeroCopilotHead" critical={false} fallback={null}>
          <HeroCopilotHead containerEl={containerEl} />
        </ErrorBoundary>
      ) : null}
      <DitherBackground />
    </>
  )
}
